/** @format */

import React from "react";
import { Box } from "./MuiCompat";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";

const fontInter = `'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif`;
const fontMono = `'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace`;

interface Pillar {
  Icon: React.ComponentType<{ sx?: object }>;
  stat: { value: string; suffix: string };
  title: string;
  body: string;
  specs: string[];
}

const pillars: Pillar[] = [
  {
    Icon: LockOutlinedIcon,
    stat: { value: "0%", suffix: "data shared" },
    title: "Your data stays yours.",
    body: "End-to-end encrypted in transit and at rest. Never sold, never used to train models for anyone else.",
    specs: [
      "E2E encrypted",
      "Export or delete anytime",
      "Zero training egress",
    ],
  },
  {
    Icon: VerifiedOutlinedIcon,
    stat: { value: "100%", suffix: "verifiable" },
    title: "Verifiable actions.",
    body: "Agent behavior runs on formal systems with strict guarantees that ensure reliability and correctness.",
    specs: [
      "Formal coordination",
      "Verifiable behavior",
      "Reliability where it matters",
    ],
  },
  {
    Icon: AutoStoriesOutlinedIcon,
    stat: { value: "20+", suffix: "patents · papers" },
    title: "Twenty years of research.",
    body: "Backed by peer-reviewed publications and patents in distributed AI, privacy, and connected systems.",
    specs: [
      "Published research in top journals",
      "Covered by leading tech media",
      "Patents and awards",
    ],
  },
];

const PillarCard: React.FC<{ pillar: Pillar }> = ({ pillar }) => {
  const Icon = pillar.Icon;
  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        borderRadius: "20px",
        border: "1px solid rgba(255, 255, 255, 0.06)",
        background:
          "linear-gradient(180deg, rgba(17, 18, 20, 0.5) 0%, rgba(7, 8, 10, 0.65) 100%)",
        backdropFilter: "blur(14px)",
        boxShadow:
          "0 24px 60px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
        padding: { xs: "24px", md: "30px" },
        display: "flex",
        flexDirection: "column",
        gap: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Box
            sx={{
              fontFamily: fontInter,
              fontWeight: 600,
              fontSize: "clamp(2.6rem, 5vw, 4rem)",
              lineHeight: 0.96,
              letterSpacing: "-0.028em",
              background:
                "linear-gradient(180deg, #FFFFFF 0%, rgba(86, 194, 255, 0.78) 130%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {pillar.stat.value}
          </Box>
          <Box
            sx={{
              mt: "10px",
              fontFamily: fontMono,
              fontSize: "11px",
              fontWeight: 500,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(255, 255, 255, 0.5)",
            }}
          >
            {pillar.stat.suffix}
          </Box>
        </Box>
        <Icon
          sx={{
            fontSize: { xs: 44, md: 56 },
            color: "rgba(255, 255, 255, 0.82)",
            filter: "drop-shadow(0 2px 14px rgba(86, 194, 255, 0.28))",
            flexShrink: 0,
          }}
        />
      </Box>

      <Box
        component="h3"
        sx={{
          m: 0,
          mt: 4,
          fontFamily: fontInter,
          fontWeight: 600,
          fontSize: "clamp(1.2rem, 1.7vw, 1.4rem)",
          lineHeight: 1.22,
          letterSpacing: "-0.012em",
          color: "var(--color-snow)",
        }}
      >
        {pillar.title}
      </Box>

      <Box
        sx={{
          mt: 2,
          mb: 3,
          fontFamily: fontInter,
          fontSize: "14.5px",
          lineHeight: 1.55,
          color: "rgba(255, 255, 255, 0.6)",
          flex: 1,
        }}
      >
        {pillar.body}
      </Box>

      <Box
        sx={{
          pt: "16px",
          borderTop: "1px solid rgba(255, 255, 255, 0.06)",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {pillar.specs.map((spec) => (
          <Box
            key={spec}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontFamily: fontMono,
              fontSize: "11.5px",
              fontWeight: 500,
              letterSpacing: "0.04em",
              color: "rgba(255, 255, 255, 0.7)",
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                color: "rgba(86, 194, 255, 0.85)",
                fontWeight: 600,
                fontSize: "11px",
              }}
            >
              ✓
            </Box>
            {spec}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Trust: React.FC = () => {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        py: { xs: 10, md: 14 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "var(--page-max-width)",
          mx: "auto",
          px: { xs: 3, sm: 5, md: 8 },
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            fontFamily: fontInter,
            fontWeight: 600,
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: "var(--leading-heading)",
            letterSpacing: "var(--tracking-heading-lg)",
            color: "var(--color-snow)",
          }}
        >
          Built to be trusted.
        </Box>
        <Box
          sx={{
            mt: 2,
            maxWidth: "640px",
            fontFamily: fontInter,
            fontSize: "var(--text-subheading)",
            lineHeight: "var(--leading-subheading)",
            letterSpacing: "var(--tracking-subheading)",
            color: "var(--color-slate-200)",
          }}
        >
          Tight privacy and security. Reliable and verifiable. Backed by two
          decades of experience and published research.
        </Box>

        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: "20px", md: "24px" },
            alignItems: "stretch",
          }}
        >
          {pillars.map((pillar) => (
            <PillarCard key={pillar.title} pillar={pillar} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Trust;
